@if (awaitingProcessing()) {
<div class="rounded-xl border border-border bg-card p-10 text-center shadow-sm">
    <h3 class="font-heading text-xl font-bold text-foreground">
        {{ status() === ScreeningKanBanStatusEnum.Not_Ready ? 'Not ready yet' : 'Processing your brief' }}
    </h3>
    <p class="mx-auto mt-2 max-w-[52ch] text-sm text-muted-foreground">
        {{ status() === ScreeningKanBanStatusEnum.Not_Ready
        ? 'This brief hasn’t been sent for processing yet, so there are no rules to review.'
        : 'We\'re reading the brief and turning it into checkable rules. This usually takes a few minutes.' }}
    </p>
</div>
} @else {

<div class="h-full w-full flex flex-col overflow-hidden">
    <div class="flex-1 w-full overflow-hidden">
        <div class="flex h-full min-h-0 flex-col gap-7 overflow-y-auto">

            <!-- ===== the brief as a document + sidebar ===== -->
            <div class="grid gap-4 lg:grid-cols-[1fr_306px]">

                <div class="overflow-hidden rounded-xl border border-border bg-card shadow-sm">

                    <div class="border-b border-gray-200 px-3 py-3 dark:border-white/10 shrink-0 bg-muted/50">
                        <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                            <div class="flex flex-row items-center justify-start gap-x-2">

                                <div class="flex flex-col items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-5 text-accent">
                                        <path
                                            d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
                                        <path d="M14 2v5a1 1 0 0 0 1 1h5" />
                                        <path d="m9 15 2 2 4-4" />
                                    </svg>
                                </div>
                                <div class="flex flex-col">
                                    <h3 class="text-sm font-semibold text-primary">The screening brief</h3>
                                    <p class="hidden sm:block mt-0.5 text-xs text-secondary">The creator requirements
                                        set by
                                        you.</p>
                                </div>
                            </div>
                            @if (results.deliverables().length) {
                            <div class="shrink-0 flex flex-row items-center gap-x-2">
                                <span
                                    class="ml-auto rounded-full bg-muted-foreground/15 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                                    Deliverable 1 of {{ results.deliverables().length }}
                                </span>
                            </div>
                            }
                        </div>
                    </div>



                    <dl class="m-0 px-5 py-4">
                        @if (results.brief()?.contentRequirements; as requirements) {
                        <dt class="mt-5 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                            Content requirements
                        </dt>
                        <dd class="m-0 mt-2 text-sm leading-relaxed text-foreground/80 [&_li]:ml-4 [&_li]:list-disc"
                            [innerHTML]="requirements"></dd>
                        }

                        @if (restrictionGroups().length === 0 && results.brief()?.contentDonts) {
                        <dt class="mt-5 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                            Content restrictions
                        </dt>
                        <dd class="m-0 mt-2 text-sm leading-relaxed text-foreground/80 [&_li]:ml-4 [&_li]:list-disc"
                            [innerHTML]="results.brief()!.contentDonts"></dd>
                        }

                        <!-- @if (results.brief()?.briefText; as briefText) {
                            <dt class="mt-5 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">Brief text</dt>
                            <dd class="m-0 mt-2 text-sm leading-relaxed text-foreground/80 [&_li]:ml-4 [&_li]:list-disc"
                                [innerHTML]="briefText"></dd>
                            } -->

                        @if (results.brief()?.screenUntil; as screenUntil) {
                        <dt class="mt-5 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                            Screening content from
                        </dt>
                        <dd class="m-0 mt-2 text-sm text-foreground/80">{{ screenUntil | date:'d MMM yyyy' }}</dd>
                        }
                    </dl>
                </div>

                <!-- sidebar -->
                <aside class="flex flex-col gap-3">

                    @if (results.product(); as product) {
                    <div class="rounded-xl border border-border bg-card p-4 shadow-sm">
                        <h3
                            class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <path d="m7.5 4.27 9 5.15" />
                                <path
                                    d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                                <path d="m3.3 7 8.7 5 8.7-5" />
                                <path d="M12 22V12" />
                            </svg>
                            Product
                        </h3>
                        <div class="mt-2.5 flex items-center gap-3">
                            @if (product.productImage) {
                            <span class="block size-10 shrink-0 overflow-hidden rounded-md">
                                <atom-ldg-img inputClass="object-cover" w="full" radius="md"
                                    [src]="imageService.getImageSource(product.productImage, 'gallery')"></atom-ldg-img>
                            </span>
                            }
                            <span class="min-w-0">
                                <span class="block truncate font-heading text-base font-bold text-foreground">{{
                                    product.productName }}</span>
                                @if (product.productCategory) {
                                <span class="block truncate text-[11px] text-muted-foreground">{{
                                    product.productCategory
                                    }}</span>
                                }
                            </span>
                        </div>
                    </div>
                    }

                    <div class="rounded-xl border border-border bg-card p-4 shadow-sm">
                        <h3
                            class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <path d="m22 22-5.5-5.5" />
                                <path d="M3 11a8 8 0 1 0 16 0 8 8 0 0 0-16 0Z" />
                                <path d="M8 11h6" />
                            </svg>
                            Screening type
                        </h3>
                        <p class="m-0 mt-2 font-heading text-xl font-bold text-foreground">Instant content review</p>
                        <p class="m-0 mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">
                            Every piece of content a creator posts against this brief is evaluated the moment it goes
                            live.
                        </p>
                    </div>

                    <div class="rounded-xl border border-border bg-card p-4 shadow-sm">
                        <h3
                            class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                                <path d="m9 15 2 2 4-4" />
                            </svg>
                            Reporting
                        </h3>
                        <p class="m-0 mt-2 font-heading text-xl font-bold text-foreground">{{ reportTypeLabel() }}</p>
                        <p class="m-0 mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">{{ reportTypeBody() }}
                        </p>
                        @if (results.brief()?.reportPrompt; as prompt) {
                        <p
                            class="m-0 mt-2.5 whitespace-pre-line rounded-md bg-muted p-2.5 text-[12.5px] leading-relaxed text-foreground/80">
                            {{ prompt }}</p>
                        }
                    </div>

                    <div class="rounded-xl border border-border bg-card p-4 shadow-sm">
                        <h3
                            class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <line x1="4" x2="20" y1="9" y2="9" />
                                <line x1="4" x2="20" y1="15" y2="15" />
                                <line x1="10" x2="8" y1="3" y2="21" />
                                <line x1="16" x2="14" y1="3" y2="21" />
                            </svg>
                            Identifiers
                        </h3>
                        <dl class="m-0 mt-2.5 flex flex-col gap-2">
                            <div class="flex items-center justify-between gap-3 text-[12.5px]">
                                <dt class="text-muted-foreground">Brief ID</dt>
                                <dd class="m-0 truncate font-mono text-[11px] text-foreground">{{ results.briefId() }}
                                </dd>
                            </div>
                            <div class="flex items-center justify-between gap-3 text-[12.5px]">
                                <dt class="text-muted-foreground">Internal ID</dt>
                                <dd class="m-0 font-mono text-[11px]"
                                    [class]="results.briefIntId() ? 'text-foreground' : 'text-muted-foreground/70'">
                                    {{ results.briefIntId() ?? 'Not assigned' }}
                                </dd>
                            </div>
                            @if (results.briefType(); as type) {
                            <div class="flex items-center justify-between gap-3 text-[12.5px]">
                                <dt class="text-muted-foreground">Type</dt>
                                <dd class="m-0 font-mono text-[11px] text-foreground">{{ type }}</dd>
                            </div>
                            }
                            @if (results.organisationId(); as org) {
                            <div class="flex items-center justify-between gap-3 text-[12.5px]">
                                <dt class="text-muted-foreground">Organisation</dt>
                                <dd class="m-0 truncate font-mono text-[11px] text-foreground">{{ org }}</dd>
                            </div>
                            }
                        </dl>
                    </div>
                </aside>
            </div>

            @if (screenFrom()) {
            <section class="w-full shrink-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <div class="shrink-0 border-b border-gray-200 bg-muted/50 px-3 py-3 dark:border-white/10">
                    <div class="flex flex-row items-center justify-start gap-x-2">
                        <div class="flex flex-col items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-5 text-accent" aria-hidden="true">
                                <path d="M8 2v4" />
                                <path d="M16 2v4" />
                                <rect width="18" height="18" x="3" y="4" rx="2" />
                                <path d="M3 10h18" />
                            </svg>
                        </div>
                        <div class="flex flex-col">
                            <h3 class="text-sm font-semibold text-primary">Screening window</h3>
                            <p class="mt-0.5 hidden text-xs text-secondary sm:block">{{ windowCaption() }}</p>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col gap-4 p-4">
                    <div class="flex items-center justify-between gap-3 rounded-xl bg-muted px-4 py-3">
                        <div class="flex min-w-0 items-center gap-4">
                            <div>
                                <p class="m-0 text-xs font-semibold uppercase tracking-wide text-secondary">From</p>
                                <p class="m-0 text-base font-bold text-primary">{{ fromLabel() }}</p>
                            </div>
                            <span class="text-secondary" aria-hidden="true">→</span>
                            <div>
                                <p class="m-0 text-xs font-semibold uppercase tracking-wide text-secondary">To</p>
                                <p class="m-0 text-base font-bold text-primary">{{ toLabel() }}</p>
                            </div>
                        </div>
                        @if (dayCount(); as days) {
                        <p
                            class="m-0 shrink-0 rounded-full bg-brand-studio/10 px-3 py-1 text-xs font-semibold text-brand-studio">
                            {{ days }} days</p>
                        }
                    </div>

                    <div>
                        <div class="relative pt-5">
                            <p class="absolute right-0 top-0 m-0 text-xs text-secondary">{{ endMarkerLabel() }}</p>
                            <div class="relative h-2 rounded-full bg-border" aria-hidden="true">
                                <div class="absolute inset-y-0 rounded-full bg-brand-studio"
                                    [style.left.%]="fill().left" [style.width.%]="fill().width"></div>
                                <span class="absolute -top-1 right-0 h-4 w-px bg-gray-900 dark:bg-white"></span>
                            </div>
                        </div>
                        <div class="mt-2 flex justify-between text-xs text-secondary">
                            <p class="m-0">{{ yearAgoLabel() }}</p>
                            <p class="m-0">{{ toLabel() }}</p>
                        </div>
                    </div>
                </div>
            </section>
            }

            <!-- ===== the user's own red flags ===== -->
            @if (restrictionGroups().length) {
            <section>
                <div class="mb-3">
                    <h3 class="m-0 font-heading text-2xl font-bold tracking-[-0.02em] text-foreground">
                        What you asked us to look out for
                    </h3>
                    <p class="m-0 mt-1.5 max-w-[64ch] text-[13px] leading-relaxed text-muted-foreground">
                        The red flags you set when you built this brief. SHOUT AI reads these alongside the brief
                        itself.
                    </p>
                </div>
                <div class="grid gap-3 md:grid-cols-2">
                    @for (group of restrictionGroups(); track group.label) {
                    <div class="rounded-xl border border-border bg-card p-4 shadow-sm">
                        <h4 class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{{
                            group.label }}
                        </h4>
                        <ul class="m-0 mt-2 flex list-none flex-col gap-1.5 p-0">
                            @for (item of group.items; track item.text) {
                            <li class="flex gap-2.5 text-sm leading-relaxed text-foreground">
                                <span aria-hidden="true"
                                    class="mt-[7px] size-1.5 shrink-0 rounded-full bg-red-500 dark:bg-red-400"></span>
                                {{ item.text }}
                            </li>
                            }
                        </ul>
                    </div>
                    }
                </div>
            </section>
            }

            <!-- ===== the rules Noodle extracted ===== -->
            <section>
                <div
                    class="w-full divide-y divide-gray-200 overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow flex flex-col min-h-0 rounded-xl border border-border shadow-sm">
                    <div class="border-b border-gray-200 px-3 py-3 dark:border-white/10 shrink-0 bg-muted/50">
                        <div class="flex flex-row items-center justify-between ">
                            <div class="flex-1 flex flex-row items-center justify-start gap-x-2">

                                <div class="flex flex-col items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-5 text-accent">
                                        <path d="M13 5h8" />
                                        <path d="M13 12h8" />
                                        <path d="M13 19h8" />
                                        <path d="m3 17 2 2 4-4" />
                                        <path d="m3 7 2 2 4-4" />
                                    </svg>
                                </div>
                                <div class="flex flex-col">
                                    <h3 class="text-sm font-semibold text-primary">Rules extracted from the brief</h3>
                                    <p class="hidden sm:block mt-0.5 text-xs text-secondary">{{ ruleSummary() }}</p>
                                </div>
                            </div>
                            <div class="shrink-0 flex min-w-0 gap-2 overflow-x-auto scrollbar-transparent" role="group"
                                aria-label="Filter rules">
                                @for (chip of filterChips(); track chip.id) {
                                <button type="button" (click)="setFilter(chip.id)"
                                    [attr.aria-pressed]="ruleFilter() === chip.id"
                                    [attr.aria-label]="filterAccessibleLabel(chip)"
                                    class="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 dark:focus-visible:outline-gray-200"
                                    [class]="filterChipClass(chip.id)">
                                    {{ chip.label }}
                                    <span aria-hidden="true" class="rounded-full px-2 py-0.5 text-xs font-bold"
                                        [class]="filterCountClass(chip.id)">{{ chip.count }}</span>
                                </button>
                                }
                            </div>
                        </div>
                    </div>
                    <div class="p-4">
                        @if (visibleRules().length) {
                        <div class="grid gap-3 md:grid-cols-2">
                            @for (entry of visibleRules(); track entry.rule.id) {
                            <article class="flex gap-3 rounded-md border border-border bg-card p-2 shadow-xs"
                                [class]="entry.rule.type === 'do' ? 'border-l-[3px] border-l-green-600' : 'border-l-[3px] border-l-red-600'">
                                <span class="grid size-[26px] shrink-0 place-items-center rounded"
                                    [class]="entry.rule.type === 'do' ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'">
                                    @if (entry.rule.type === 'do') {
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                        stroke-linejoin="round" aria-hidden="true">
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                    } @else {
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                        stroke-linejoin="round" aria-hidden="true">
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                    </svg>
                                    }
                                </span>
                                <div class="min-w-0 flex-1">
                                    <p class="m-0 text-pretty text-sm leading-relaxed text-foreground">
                                        <span class="sr-only">{{ entry.rule.type === 'do' ? 'Do: ' : "Don't: "
                                            }}</span>{{
                                        entry.rule.statement }}
                                    </p>
                                    <div class="mt-2 flex flex-wrap items-center gap-1.5">
                                        <span
                                            class="rounded bg-foreground px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-background">
                                            {{ entry.rule.questionSubType }}
                                        </span>
                                        <span
                                            class="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                                            [class]="entry.rule.subjectiveType === 1 ? 'bg-brand-studio/10 text-brand-studio-dark dark:text-brand-studio' : 'bg-teal-500/10 text-teal-700 dark:text-teal-300'">
                                            {{ entry.rule.subjectiveType === 1 ? 'Subjective' : 'Objective' }}
                                        </span>
                                        <span
                                            class="rounded border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                            {{ ruleTag(entry.rule) }}
                                        </span>

                                        @if (entry.rule.generalised && hasContext(entry.rule)) {
                                        <span
                                            class="ml-auto inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-500/15 dark:text-green-300">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                                                stroke-linecap="round" stroke-linejoin="round" class="size-3.5"
                                                aria-hidden="true">
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                            Context added
                                        </span>
                                        } @else if (entry.rule.generalised) {
                                        <span
                                            class="ml-auto inline-flex items-center gap-x-1.5 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-400/10 dark:text-yellow-500">
                                            <svg viewBox="0 0 6 6" aria-hidden="true"
                                                class="size-1.5 fill-yellow-500 dark:fill-yellow-400">
                                                <circle r="3" cx="3" cy="3" />
                                            </svg>
                                            Generalised
                                        </span>
                                        }
                                        <span class="font-mono text-[11px] text-muted-foreground"
                                            [class]="entry.rule.generalised ? '' : 'ml-auto'">{{
                                            ruleNumberLabel(entry.number) }}</span>
                                    </div>
                                </div>
                            </article>
                            }
                        </div>
                        } @else {
                        <p
                            class="flex items-center justify-center gap-2.5 rounded-xl border border-dashed border-border p-8 text-sm text-muted-foreground">
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <path d="m13.5 8.5-5 5" />
                                <path d="m8.5 8.5 5 5" />
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.3-4.3" />
                            </svg>
                            No rules match this filter.
                        </p>
                        }
                    </div>
                </div>
            </section>


            <!-- ===== brand safety ===== -->
            @if (results.brandSafetyRules().length) {
            <section class="shrink-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <button type="button" (click)="toggleBrandSafety()" [attr.aria-expanded]="brandSafetyOpen()"
                    class="flex w-full items-center gap-3 px-5 py-4 text-left">
                    <span class="font-heading text-base font-bold text-foreground">Brand safety questions</span>
                    <span class="rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">{{
                        results.brandSafetyRules().length }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="ml-auto shrink-0 text-muted-foreground transition-transform"
                        [class]="brandSafetyOpen() ? 'rotate-180' : ''" aria-hidden="true">
                        <path d="m6 9 6 6 6-6" />
                    </svg>
                </button>
                @if (brandSafetyOpen()) {
                <ul class="m-0 flex list-none flex-col gap-2 border-t border-border p-5">
                    @for (rule of results.brandSafetyRules(); track rule.id) {
                    <li
                        class="flex gap-3 rounded-md border border-border bg-muted/50 p-3 text-sm leading-relaxed text-foreground">
                        <span class="rounded px-1.5 py-0.5 text-xs font-bold uppercase"
                            [class]="rule.type === 'do' ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'">
                            {{ rule.type === 'do' ? 'Do' : "Don't" }}
                        </span>
                        {{ rule.statement }}
                    </li>
                    }
                </ul>
                }
            </section>
            } @else {
            <section
                class="shrink-0 rounded-xl border border-dashed border-border bg-card px-5 py-8 text-center shadow-sm">
                <h3 class="m-0 font-heading text-base font-bold text-foreground">Brand safety questions</h3>
                <p
                    class="m-0 mx-auto mt-1.5 flex max-w-prose items-center justify-center gap-2.5 text-sm text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="shrink-0" aria-hidden="true">
                        <path
                            d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                        <path d="m9 12 2 2 4-4" />
                    </svg>
                    No brand safety questions were extracted from this brief.
                </p>
            </section>
            }

            @if (results.deliverables().length) {
            <section class="shrink-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <button type="button" (click)="toggleDeliverables()" [attr.aria-expanded]="deliverablesOpen()"
                    class="flex w-full items-center gap-3 px-5 py-4 text-left">
                    <span class="font-heading text-base font-bold text-foreground">Concepts &amp; deliverables</span>
                    <span class="rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">{{
                        results.deliverables().length }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="ml-auto shrink-0 text-muted-foreground transition-transform"
                        [class]="deliverablesOpen() ? 'rotate-180' : ''" aria-hidden="true">
                        <path d="m6 9 6 6 6-6" />
                    </svg>
                </button>
                @if (deliverablesOpen()) {
                <ul class="m-0 flex list-none flex-col gap-3 border-t border-border p-5">
                    @for (item of results.deliverables(); track item.id) {
                    <li class="rounded-md border border-border bg-muted/50 p-3.5">
                        <p class="m-0 font-heading text-sm font-bold text-foreground">{{ item.deliverable }}</p>
                        <p class="m-0 mt-1.5 text-sm leading-relaxed text-foreground/80">{{ item.summary }}</p>
                    </li>
                    }
                </ul>
                }
            </section>
            } @else {
            <section
                class="shrink-0 rounded-xl border border-dashed border-border bg-card px-5 py-8 text-center shadow-sm">
                <h3 class="m-0 font-heading text-base font-bold text-foreground">Concepts &amp; deliverables</h3>
                <p
                    class="m-0 mx-auto mt-1.5 flex max-w-prose items-center justify-center gap-2.5 text-sm text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="shrink-0" aria-hidden="true">
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                        <path d="M10 9H8" />
                        <path d="M16 13H8" />
                        <path d="M16 17H8" />
                    </svg>
                    No concepts or deliverables were extracted from this brief.
                </p>
            </section>
            }


        </div>
    </div>
    <div class="shrink-0 w-full pt-2">
        <!-- ===== activate ===== -->
        @if (canActivate()) {
        <div
            class="shrink-0 rounded-xl bg-fuchsia-50 px-5 py-4 dark:bg-fuchsia-500/10 dark:outline dark:outline-fuchsia-500/20">
            <div class="flex flex-wrap items-center gap-4">
                <p class="m-0 flex-1 text-sm text-fuchsia-700 dark:text-fuchsia-300">
                    @if (contextReady()) {
                    Context added to {{ generalisedCount() }} rules. Activate the brief to
                    <strong class="font-semibold">begin screening</strong>.
                    } @else {
                    Lock in your updates and activate the brief to <span class="font-semibold">begin screening</span>.
                    }
                </p>
                <button type="button" (click)="activateBrief()" [disabled]="isActivating()"
                    class="flashy-go-button inline-flex items-center gap-x-2 rounded-xl disabled:opacity-60">
                    {{ isActivating() ? 'Activating brief…' : 'Activate brief' }}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4" aria-hidden="true">
                        <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
                    </svg>
                </button>
            </div>
        </div>
        } @else {
        <section class="shrink-0 pt-4">
            <div class="rounded-md bg-green-50 p-4 dark:bg-green-500/10 dark:outline dark:outline-green-500/20">
                <div class="flex">
                    <div class="shrink-0">
                        <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true"
                            class="size-5 text-green-400">
                            <path
                                d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                clip-rule="evenodd" fill-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="text-sm font-medium text-green-800 dark:text-green-300">{{
                            activateUnavailableMessage() }}</p>
                    </div>
                    <div class="ml-auto pl-3">
                        <div class="-mx-1.5 -my-1.5">
                            <button type="button"
                                class="inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100 focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2 focus-visible:ring-offset-green-50 focus-visible:outline-hidden dark:bg-transparent dark:text-green-400 dark:hover:bg-green-500/10 dark:focus-visible:ring-green-500 dark:focus-visible:ring-offset-1 dark:focus-visible:ring-offset-green-900">
                                <span class="sr-only">Dismiss</span>
                                <svg viewBox="0 0 20 20" fill="currentColor" data-slot="icon" aria-hidden="true"
                                    class="size-5">
                                    <path
                                        d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        }
    </div>
</div>

}

<app-review-questions-modal
    [briefTitle]="briefTitle()"
    [totalCount]="requirementCount()"
    [generalisedCount]="pendingGeneralisedCount()"
    (skipped)="onReviewSkipped()"
    (review)="onReviewRequested()" />

<app-review-questions-drawer
    [briefTitle]="briefTitle()"
    [questions]="results.rules()"
    [questionGroup]="results.questionGroup()"
    (saved)="onContextSaved($event)" />